import React from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import { UserDetailDto } from '../api/userManagementApi';
import { UserOverviewCard } from './panel/molecules/UserOverviewCard';
import { UserRoleCard } from './panel/molecules/UserRoleCard';
import { UserBusinessDetailsCard } from './panel/molecules/UserBusinessDetailsCard';
import { UserBusinessDetailsEmptyCard } from './panel/molecules/UserBusinessDetailsEmptyCard';
import { UserPaymentDetailsCard } from './panel/molecules/UserPaymentDetailsCard';
import { UserPaymentDetailsEmptyCard } from './panel/molecules/UserPaymentDetailsEmptyCard';
import { UserLabourGroupCard } from './panel/molecules/UserLabourGroupCard';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { UserSiteAccessCard } from './panel/molecules/UserSiteAccessCard';
import { UserInvitationCard } from './panel/molecules/UserInvitationCard';
import { UserOffboardingCard } from './panel/molecules/UserOffboardingCard';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { UserStatus } from '../api/userManagementApi';

interface UserDetailsPanelProps {
  user: UserDetailDto | null;
  loading: boolean;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const UserDetailsPanel: React.FC<UserDetailsPanelProps> = ({
  user,
  loading,
}) => {
  const currentUser = useCurrentUser();
  const isCurrentUser = !!(
    user &&
    currentUser &&
    user.id === currentUser.userId
  );

  return (
    <Box sx={{ p: 3 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <CircularProgress />
        </Box>
      ) : user ? (
        <Stack spacing={3}>
          {/* User Overview Card */}
          <UserOverviewCard
            user={user}
            formatDate={formatDate}
            isCurrentUser={isCurrentUser}
          />

          {/* User Role Card */}
          <UserRoleCard
            userId={user.id}
            roles={user.roles}
            status={user.status}
            isCurrentUser={isCurrentUser}
          />

          {/* Business Details Card */}
          {user.businessDetails ? (
            <UserBusinessDetailsCard businessDetails={user.businessDetails} />
          ) : (
            <UserBusinessDetailsEmptyCard />
          )}

          {/* Payment Details Card - permission gated */}
          <PermissionGate
            permission={Permissions.Users.PaymentDetails.Read}
            fallback={null}
          >
            {user.paymentDetails ? (
              <UserPaymentDetailsCard paymentDetails={user.paymentDetails} />
            ) : (
              <UserPaymentDetailsEmptyCard />
            )}
          </PermissionGate>

          {/* Labour Group Card - hidden for offboarded users */}
          {user.id &&
            user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <UserLabourGroupCard
                userId={user.id}
                currentGroupId={user.labourGroupId}
                currentGroupName={user.labourGroupName ?? 'Unassigned'}
              />
            )}

          {/* Site Access Card - hidden for offboarded users */}
          {user.id &&
            user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <UserSiteAccessCard
                userId={user.id}
                userName={getUserDisplayName(user)}
              />
            )}

          {/* Offboarding Card (conditional) */}
          {user.offboardingInfo && (
            <UserOffboardingCard
              offboardingInfo={user.offboardingInfo}
              formatDateTime={formatDateTime}
            />
          )}

          {/* User Invitation Card (conditional) */}
          {user.invitationInfo && (
            <UserInvitationCard
              invitedByUserName={user.invitationInfo.invitedByUserName}
              invitedAt={user.invitationInfo.invitedAt}
              formatDateTime={formatDateTime}
            />
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" align="center">
          No user details available
        </Typography>
      )}
    </Box>
  );
};

export default UserDetailsPanel;
